export interface Options {
  [key: string]: any;
}

export interface Fn {
  (options: Options): Options;
}

export interface Plugins {
  [key: string]: Fn;
}
const plugins: Plugins = {}

export const regReactPlugin = (name: string, fn: Fn) => {
  if (!name || typeof fn !== 'function') {
    console.warn(`regReactPlugin: arguments are required. [name: string, fn: function]`);
    return;
  }
  if (plugins[name]) {
    const preFn = plugins[name];
    // 需要返回 options 到先注册过的方法中，后进先出
    plugins[name] = (options: Options): Options => preFn(fn(options));
  } else {
    // 默认源码处理，不再底层处理，容易诱发问题，这边单独再还原处理一次
    plugins[name] = (options: Options): any => {
      const newOptions = fn(options);
      if (name === 'setAttribute') {
        const { node, attributeNamespace, attributeName, propertyName, attributeValue } = newOptions;
        // 只允许修改 attributeValue, 至于对于 dom 对象的操作也可以添加，但是要慎重，一般用来拦截修改样式名链接等
        if (propertyName) {
          node[propertyName] = attributeValue;
        } else if (attributeNamespace) {
          node.setAttributeNS(attributeNamespace, attributeName, attributeValue);
        } else {
          node.setAttribute(attributeName, attributeValue);
        }
      } else if (name === 'setTextContent') {
        const { node, text } = newOptions;
        // 只允许返回 text 为文本修改或者替换为元素 dom 对象修改两种情况
        // dom 对象多层注册需要判断是否往下级元素添加子节点处理，一般替换文本为 span，一定要判断 nodeType，react 默认处理不会是注释节点
        if (typeof text === 'string') {
          if (text) {
            const firstChild = node.firstChild;
            if (
              firstChild &&
              firstChild === node.lastChild &&
              firstChild.nodeType === 3
            ) {
              firstChild.nodeValue = text;
              return;
            }
          }
          node.textContent = text;
        } else if(typeof node.appendChild === 'function' && text.nodeType === 1) {
          node.textContent = '';
          node.appendChild(text);
        }
      } else if (name === 'setInnerHTML') {
        const { node, html } = newOptions;
        // 只允许修改 html 为新的富文本
        node.innerHTML = html;
      } else if (name === 'appendChild') {
        const { parentInstance, child } = newOptions;
        // 只允许修改 child 为新的元素 dom 对象
        // dom 对象多层注册需要判断是否往下级元素添加子节点处理，一般替换文本为 span，一定要判断 nodeType，react 默认处理不会是注释节点
        parentInstance.appendChild(child);
      } else if (name === 'insertBefore') {
        const { container, child, beforeChild } = newOptions;
        // 只允许修改 child 为新的元素 dom 对象，一定要判断 nodeType
        // 如果是注释节点，要插入到父节点内
        if (container.nodeType === 8) {
          container.parentNode.insertBefore(child, beforeChild);
        } else {
          container.insertBefore(child, beforeChild);
        }
      } else if (name === 'setNodeValue') {
        // 这个方法很少用到，主要是 setTextContent
        const { textInstance, newText } = newOptions;
        // 只允许返回 newText 为文本修改或者替换为元素 dom 对象修改两种情况
        // dom 对象多层注册需要判断是否往下级元素添加子节点处理，一般替换文本为 span，一定要判断 nodeType，react 默认处理不会是注释节点
        if (typeof newText === 'string') {
          textInstance.nodeValue = newText;
        } else if(newText.nodeType === 1 && textInstance.nodeType === 3) {
          const parentElement = textInstance.parentElement;
          const prevElement = textInstance.previousSibling;
          if (parentElement) {
            newText.setAttribute('is-urp-set-node-value', 'true');
            if (prevElement && prevElement.nodeType === 1 && prevElement.getAttribute('is-urp-set-node-value')) {
              parentElement.replaceChild(newText, prevElement);
            } else {
              parentElement.insertBefore(newText, textInstance);
            }
            textInstance.nodeValue = '';
          }
        }
      } else if (name === 'createTextInstance') {
        // 这个方法很少用到，主要是 setTextContent
        const { text, rootContainerInstance, internalInstanceHandle, precacheFiberNode } = newOptions;
        // 只允许返回 text 为文本修改或者替换为元素 dom 对象修改两种情况
        // dom 对象多层注册需要判断是否往下级元素添加子节点处理，一般替换文本为 span，一定要判断 nodeType，react 默认处理不会是注释节点
        let textNode: any = null;
        if (text.nodeType === 1) {
          textNode = text;
        } else {
          let doc = rootContainerInstance.nodeType === 8 ? rootContainerInstance : rootContainerInstance.ownerDocument;
          textNode = doc.createTextNode(typeof text === 'string' ? text : '');
        }
        precacheFiberNode(internalInstanceHandle, textNode);
        return textNode;
      } else if (name === 'shouldEnableProfilerTimer') {
        const { enableProfilerTimer } = newOptions || {};
        return enableProfilerTimer || false;
      }
    };
  }
}

export function getReactPlugin(name?: string) {
  if (name && plugins[name]) {
    return plugins[name];
  }
  return plugins;
}

/* setAttribute */
/*
plugins.setAttribute({
  node,
  isCustomComponentTag,
  propertyInfo,
  attributeNamespace,
  attributeName,
  propertyName,
  attributeValue,
})

// 源代码 1
(node: any)[propertyName] = value;

// 源代码 2
if (attributeNamespace) {
  node.setAttributeNS(attributeNamespace, attributeName, attributeValue);
} else {
  node.setAttribute(attributeName, attributeValue);
}
*/

/* setTextContent */
/*
plugins.setTextContent({
  node,
  text,
});

// 源代码 TEXT_NODE = 3
if (text) {
  const firstChild = node.firstChild;

  if (
    firstChild &&
    firstChild === node.lastChild &&
    firstChild.nodeType === TEXT_NODE
  ) {
    firstChild.nodeValue = text;
    return;
  }
}
node.textContent = text;
*/

/* setInnerHTML */

/*
plugins.setInnerHTML({
  node,
  html,
});

// 源代码
node.innerHTML = (html: any);
*/


/* appendChild */
/*
plugins.appendChild({
  parentInstance,
  child
});

// 源代码
parentInstance.appendChild(child);
*/

/* insertBefore */
/*
plugins.insertBefore({
  container,
  child,
  beforeChild
});

// 源代码 COMMENT_NODE = 8
if (container.nodeType === COMMENT_NODE) {
  (container.parentNode: any).insertBefore(child, beforeChild);
} else {
  container.insertBefore(child, beforeChild);
}
*/

/* setNodeValue */
/*
plugins.setNodeValue({
  textInstance,
  newText,
});

// 源代码
textInstance.nodeValue = newText;
*/

/* createTextInstance */
/*
plugins.createTextInstance({
  text,
  rootContainerInstance,
  internalInstanceHandle,
  precacheFiberNode,
})

// 源代码
const textNode: TextInstance = createTextNode(text, rootContainerInstance);
precacheFiberNode(internalInstanceHandle, textNode);
return textNode;
*/
